import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "../lib/api";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "./ui/hover-card";

// Match-order: longest term first so "Da Hong Pao" beats "Pao", "Black Tea" beats "Tea".
// Skip very short stems entirely to avoid false matches inside common English words.
const MIN_MATCH_LEN = 4;


// Cache the glossary list across renders (only fetch once per session).
let cachedTerms = null;

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Strip a glossary term down to its primary matching phrase:
 *   "Black Tea (Hong Cha)"  -> "Black Tea"
 *   "FTGFOP"                 -> "FTGFOP"
 *   "Fixing (Kill-Green / Sha Qing)" -> "Fixing"
 */
function primaryMatchPhrase(term) {
  return term.split("(")[0].trim();
}

/**
 * GlossaryLinker
 *
 * Takes plain post body text (paragraphs separated by blank lines) and
 * renders it as <p> elements where the first occurrence of any glossary
 * term in each paragraph is wrapped in a HoverCard linking to that term's
 * page. Hover (desktop) or tap (mobile) reveals the short definition.
 *
 * Designed for editorial readability — at most one tooltip-link per term
 * per paragraph, longest matches first, never overlaps.
 */
export default function GlossaryLinker({ text, postSlug }) {
  const [terms, setTerms] = useState(cachedTerms || []);

  useEffect(() => {
    if (cachedTerms) return;
    http.get("/glossary").then((r) => {
      const list = r.data.entries
        .map((e) => ({ ...e, match: primaryMatchPhrase(e.term) }))
        .filter((e) => e.match.length >= MIN_MATCH_LEN)
        // Longest match first — prevents shorter sub-terms hijacking the link
        .sort((a, b) => b.match.length - a.match.length);
      cachedTerms = list;
      setTerms(list);
    });
    // MIN_MATCH_LEN, primaryMatchPhrase, escapeRegex are module constants/functions;
    // `http`/`setTerms` are stable refs. Effect runs once per session (cachedTerms guard).
  }, []);  

  // Pre-compile a regex per term once `terms` is available.
  const compiledTerms = useMemo(
    () =>
      terms.map((t) => ({
        ...t,
        regex: new RegExp(`\\b${escapeRegex(t.match)}\\b`, "i"),
      })),
    [terms]
  );

  // Always derive paragraphs from text so first-paint (before terms load) still works.
  const paragraphs = useMemo(() => text.split("\n\n"), [text]);

  return paragraphs.map((para, paraIdx) => {
    // First-occurrence-per-paragraph tracking for glossary terms.
    const used = new Set();
    const linkify = (str, keyPrefix) => linkifyTerms(str, compiledTerms, used, keyPrefix);
    return (
      <p key={`${postSlug}-p-${paraIdx}`}>
        {renderInline(para, `${paraIdx}`, linkify)}
      </p>
    );
  });
}

// [text](/path) | **bold** | *italic*
const INLINE_RE = /\[([^\]]+)\]\((\/[^)\s]+)\)|\*\*(.+?)\*\*|(?<![*\w])\*(?!\s)([^*\n]+?)(?<!\s)\*(?![*\w])/g;

/**
 * Render a paragraph's inline markdown (internal links, bold, italic) and pass
 * every plain-text run through `linkify` (glossary auto-linking).
 */
function renderInline(text, keyPrefix, linkify) {
  if (!text) return text;
  const out = [];
  let cursor = 0;
  let i = 0;
  const re = new RegExp(INLINE_RE.source, "g"); // fresh instance: recursion must not share lastIndex
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > cursor) out.push(...toArray(linkify(text.slice(cursor, m.index), `${keyPrefix}-${i}`)));
    const k = `${keyPrefix}-${i++}`;
    if (m[1] !== undefined) {
      out.push(
        <Link
          key={`link-${k}`}
          to={m[2]}
          className="text-[#1e4a35] underline decoration-dotted underline-offset-4 hover:text-[#151e1a] transition-colors"
          data-testid="post-internal-link"
        >
          {m[1]}
        </Link>
      );
    } else if (m[3] !== undefined) {
      out.push(<strong key={`b-${k}`}>{renderInline(m[3], `${k}-b`, linkify)}</strong>);
    } else {
      out.push(<em key={`i-${k}`}>{toArray(linkify(m[4], `${k}-i`))}</em>);
    }
    cursor = m.index + m[0].length;
  }
  if (cursor < text.length) out.push(...toArray(linkify(text.slice(cursor), `${keyPrefix}-tail`)));
  return out;
}

const toArray = (v) => (Array.isArray(v) ? v : [v]);

/** Wrap first occurrence of each glossary term (per paragraph) in a hover link. */
function linkifyTerms(str, compiledTerms, used, keyPrefix) {
  if (!str || !compiledTerms.length) return str;
  const matches = [];
  for (const t of compiledTerms) {
    if (used.has(t.slug)) continue;
    const m = t.regex.exec(str);
    if (!m) continue;
    const start = m.index;
    const end = start + m[0].length;
    if (matches.some((x) => start < x.end && end > x.start)) continue;
    matches.push({ start, end, term: t, original: m[0] });
    used.add(t.slug);
  }
  if (!matches.length) return str;
  matches.sort((a, b) => a.start - b.start);
  const parts = [];
  let cursor = 0;
  matches.forEach((m, i) => {
    if (m.start > cursor) parts.push(str.slice(cursor, m.start));
    parts.push(<GlossaryHoverLink key={`term-${keyPrefix}-${i}`} term={m.term} text={m.original} />);
    cursor = m.end;
  });
  if (cursor < str.length) parts.push(str.slice(cursor));
  return parts;
}

function GlossaryHoverLink({ term, text }) {
  return (
    <HoverCard openDelay={120} closeDelay={120}>
      <HoverCardTrigger asChild>
        <Link
          to={`/glossary/${term.slug}`}
          className="text-[#151e1a] underline decoration-dotted decoration-[#1e4a35]/60 underline-offset-4 hover:decoration-[#1e4a35] hover:text-[#1e4a35] transition-colors"
          data-testid={`glossary-link-${term.slug}`}
        >
          {text}
        </Link>
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="center"
        className="w-80 border bg-white"
        style={{ borderColor: "var(--border)" }}
      >
        <div className="space-y-2">
          <div className="flex items-baseline gap-2 flex-wrap">
            <p className="font-serif text-lg font-medium text-[#151e1a]">
              {term.term}
            </p>
            {term.pronunciation && (
              <span className="font-mono text-[9px] uppercase tracking-widest text-[#66726d]">
                /{term.pronunciation}/
              </span>
            )}
          </div>
          <p className="text-sm text-[#404e48] font-light leading-relaxed">
            {term.short_def}
          </p>
          <p className="font-mono text-[9px] uppercase tracking-widest text-[#1e4a35]">
            {term.category} · open glossary →
          </p>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
